// ============================================================
// usePrefetchOnHover — React Router prefetch hint
//
// Returns an object you spread on links to prefetch the route's
// chunk when the user hovers/focuses the link.
//
// Usage:
//   import { usePrefetchOnHover } from '@/hooks/usePrefetchOnHover';
//
//   function MyLink() {
//     const prefetch = usePrefetchOnHover(() => import('@/pages/Shop'));
//     return <Link to="/shop" {...prefetch}>Shop</Link>;
//   }
//
// Or simpler — pass a static `to` and let the hook decide:
//   const prefetch = usePrefetchOnHover(); // no-op stub
//
// To take advantage of route-level code-splitting prefetch, you'd need
// to map paths to dynamic imports. For now this is a no-op safe wrapper
// that documents intent — extend later when routes are migrated to
// lazy chunks.
// ============================================================

import { useCallback } from 'react';

export function usePrefetchOnHover(importFn) {
  const onPointerEnter = useCallback(() => {
    if (typeof importFn === 'function') {
      // Fire the dynamic import. Browser caches the chunk; React picks it up.
      importFn().catch(() => { /* network errors silently ignored */ });
    }
  }, [importFn]);
  const onFocus = onPointerEnter;
  return { onPointerEnter, onFocus };
}
